import "@/styles/index.css";
import { showBootError } from "./boot-error";

/**
 * Einstieg: lädt die App erst hier, damit ein Fehler beim Laden (Inhaltspaket oder Programmteil kommt nicht an)
 * im Klartext erscheint statt als leere Seite. Alles Weitere steht in `app.tsx`.
 */
/** Der Browser feuert `beforeinstallprompt` einmal und früh; die App lädt jetzt später. `offline/install.ts` holt den Fang ab. */
window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  (window as unknown as { __ltInstallPrompt?: Event }).__ltInstallPrompt = event;
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root fehlt");
const root = rootEl;

/**
 * Welche Sprache gilt, entscheidet sich vor dem Laden der App (KONZEPT §15.3): `lt.lang` gewählt → App; sonst Bestand
 * auf dem Gerät (Derja-Ereignisse, auch aus der Datenbank vor M7) oder eine bestehende Sitzung → die Sprache des Bestands;
 * sonst die Sprachwahl. Die Sprachwahl lädt weder Inhalt noch Engine; die Wahl lädt die Seite neu (D50).
 */
async function start(): Promise<void> {
  const { isLegacyHost, tokenFromHash } = await import("./umzug/hosts");
  /* Alte Adresse: nur die Umzugsseite (DESIGN §11.7), weder Inhalt noch Engine; sie liest die alte Datenbank `learntunis`. */
  if (isLegacyHost()) {
    const [{ createRoot }, { createElement }, { LegacyPage }, { initTheme }] = await Promise.all([
      import("react-dom/client"),
      import("react"),
      import("./umzug/legacy-page"),
      import("./lib/theme"),
    ]);
    initTheme();
    createRoot(root).render(createElement(LegacyPage));
    return;
  }
  /*
   * Neue Adresse, Übergabe (`/umzug#t=<schlüssel>`): Der Schlüssel kommt aus der Adresse (er bleibt in keinem Verlauf) in
   * den Speicher, und die App startet mit Derja, ohne die Sprachwahl (der Lernstand der alten Adresse ist Derja).
   */
  const token = location.pathname === "/umzug" ? tokenFromHash(location.hash) : null;
  if (token !== null) {
    const [{ stashToken }, core] = await Promise.all([
      import("./umzug/pending"),
      import("./lang/core"),
    ]);
    stashToken(token);
    history.replaceState(null, "", "/umzug");
    core.storeLang("aeb");
  }
  const { resolveStartLang } = await import("./lang/resolve");
  const decision = await resolveStartLang({ me: askMe });
  if (decision === "pick") {
    const [{ createRoot }, { LangPicker }, core, { authEnabled }] = await Promise.all([
      import("react-dom/client"),
      import("./lang/picker"),
      import("./lang/core"),
      import("./auth/enabled"),
    ]);
    const { createElement } = await import("react");
    createRoot(root).render(
      createElement(LangPicker, {
        onChoose: (chosen) => {
          core.storeLang(chosen);
          core.markLangHintSeen();
          location.reload();
        },
        onLogin: authEnabled()
          ? () => {
              try {
                sessionStorage.setItem(core.LANG_LOGIN_KEY, "1");
              } catch {
                /* ohne sessionStorage landet man wieder in der Sprachwahl */
              }
              location.reload();
            }
          : undefined,
      }),
    );
    return;
  }
  if (decision === "reload") {
    location.reload();
    return;
  }
  if (decision === "login") history.replaceState(null, "", "/login");
  await import("./app");
}

/** Gibt es schon eine Sitzung (Cookie ohne `lt.lang`)? Nur mit Login in diesem Build und nur kurz warten. */
async function askMe() {
  const { authEnabled } = await import("./auth/enabled");
  if (!authEnabled()) return null;
  const { fetchMe } = await import("./auth/api");
  return fetchMe(AbortSignal.timeout(2500));
}

start().catch((error: unknown) => {
  console.error(error);
  showBootError(root);
});
